/**
 * Gerenciador de mídias reutilizável.
 *
 * Um único componente atende o modelo mestre, a edição real e a ministração
 * do ministrador: listar, enviar arquivo, vincular YouTube/Spotify/outro link,
 * baixar, abrir, remover e incorporar ao Modelo Oficial.
 */

import { h, clear, formatBytes } from '../ui/dom.js';
import {
  button,
  field,
  input,
  select,
  openModal,
  toast,
  badge,
  emptyState,
} from '../ui/components.js';
import { api, ApiError } from '../api.js';
import { kindIcon, downloadAsset } from '../ui/media.js';
import {
  MEDIA_KIND_LABELS,
  type MediaAsset,
  type MediaKind,
  type MediaProvider,
  type MediaScope,
} from '../types.js';

export interface MediaManagerOptions {
  /** Título exibido no cabeçalho do painel. */
  title: string;
  subtitle?: string;
  scope: MediaScope;
  ownerId: string;
  /** Endpoint de listagem e de upload (multipart) deste dono. */
  basePath: string;
  canWrite: boolean;
  canIncorporate: boolean;
  /** Rótulo de orientação quando não há permissão de escrita. */
  readOnlyHint?: string;
  onChanged?: () => void;
}

const KIND_GROUPS: Array<{ label: string; kinds: MediaKind[] }> = [
  { label: 'Roteiros e documentos', kinds: ['ROTEIRO', 'PDF'] },
  { label: 'Apresentações de slides', kinds: ['SLIDES'] },
  { label: 'Áudio, vídeo e playlists', kinds: ['AUDIO', 'VIDEO', 'PLAYLIST'] },
  { label: 'Imagens', kinds: ['IMAGEM'] },
];

const MEDIA_KINDS: MediaKind[] = ['ROTEIRO', 'PDF', 'SLIDES', 'AUDIO', 'VIDEO', 'IMAGEM', 'PLAYLIST'];

/** Abre o painel de materiais de uma ministração (modelo ou edição). */
export function openMediaManager(options: MediaManagerOptions): void {
  const listContainer = h('div', { class: 'stack' });
  const countLabel = h('span', { class: 'card__hint', text: 'Carregando…' });

  const reload = async (): Promise<void> => {
    clear(listContainer);
    listContainer.append(h('div', { class: 'skeleton', style: { width: '55%' } }));

    try {
      const { items } = await api.get<{ items: MediaAsset[] }>(options.basePath);
      clear(listContainer);

      const total = items.length;
      const docs = items.filter((a) => a.kind === 'ROTEIRO' || a.kind === 'PDF' || a.kind === 'SLIDES').length;
      const audiovisual = items.filter((a) => a.kind === 'AUDIO' || a.kind === 'VIDEO' || a.kind === 'PLAYLIST').length;
      const images = items.filter((a) => a.kind === 'IMAGEM').length;

      countLabel.textContent =
        total === 0
          ? 'Nenhum material anexado ainda'
          : `${total} material(is) · ${docs} documento(s) · ${audiovisual} áudio/vídeo · ${images} imagem(ns)`;

      if (total === 0) {
        listContainer.append(
          emptyState(
            options.canWrite
              ? 'Nenhum material anexado. Use "Enviar arquivo" para slides, PDFs e imagens, ou "Vincular link" para YouTube e Spotify.'
              : 'Nenhum material anexado a esta ministração.',
            '◫',
          ),
        );
        return;
      }

      for (const group of KIND_GROUPS) {
        const groupItems = items.filter((asset) => group.kinds.includes(asset.kind));
        if (groupItems.length === 0) continue;

        listContainer.append(
          h('div', { class: 'media-group__title', text: group.label }),
          ...groupItems.map((asset) => renderRow(asset)),
        );
      }
    } catch (error) {
      clear(listContainer);
      listContainer.append(
        h('div', {
          class: 'field__error',
          text: error instanceof ApiError ? error.message : 'Falha ao carregar os materiais.',
        }),
      );
      countLabel.textContent = 'Erro ao carregar';
    }
  };

  const renderRow = (asset: MediaAsset): HTMLElement =>
    h(
      'div',
      { class: 'list__item media-row' },
      h('div', { class: 'list__index', text: kindIcon(asset.kind) }),
      h(
        'div',
        { class: 'list__body' },
        h(
          'div',
          { class: 'row' },
          h('span', { class: 'list__title', text: asset.title }),
          badge(MEDIA_KIND_LABELS[asset.kind], 'info'),
          asset.isOfficial ? badge('Oficial', 'success') : badge(`v${asset.version}`, 'neutral'),
          asset.provider !== 'LOCAL' ? badge(asset.provider, 'accent') : null,
        ),
        h(
          'div',
          { class: 'row small muted' },
          asset.mimeType ? h('span', { class: 'faint', text: asset.mimeType }) : null,
          asset.sizeBytes ? h('span', { class: 'faint', text: formatBytes(asset.sizeBytes) }) : null,
          asset.externalUrl
            ? h('span', { class: 'faint mono', text: truncate(asset.externalUrl, 46) })
            : null,
        ),
      ),
      h(
        'div',
        { class: 'table__actions' },
        asset.externalUrl
          ? button('Abrir', {
              size: 'sm',
              variant: 'ghost',
              onClick: () => window.open(asset.externalUrl as string, '_blank', 'noopener'),
            })
          : button('Baixar', {
              size: 'sm',
              variant: 'ghost',
              onClick: () => void downloadAsset(asset),
            }),
        options.canIncorporate && !asset.isOfficial
          ? button('Incorporar', {
              size: 'sm',
              variant: 'subtle',
              onClick: () => void incorporate(asset),
            })
          : null,
        options.canWrite
          ? button('Remover', {
              size: 'sm',
              variant: 'ghost',
              onClick: () => void remove(asset),
            })
          : null,
      ),
    );

  const incorporate = async (asset: MediaAsset): Promise<void> => {
    try {
      await api.post(`/api/media/${asset.id}/incorporate`);
      toast.success('Incorporado ao Modelo Oficial', 'Passará a valer para as próximas edições.');
      options.onChanged?.();
      await reload();
    } catch (error) {
      toast.error('Falha ao incorporar', error instanceof ApiError ? error.message : undefined);
    }
  };

  const remove = async (asset: MediaAsset): Promise<void> => {
    try {
      await api.delete(`/api/media/${asset.id}`);
      toast.success('Material removido');
      options.onChanged?.();
      await reload();
    } catch (error) {
      toast.error('Falha ao remover', error instanceof ApiError ? error.message : undefined);
    }
  };

  void reload();

  let modal: { close: () => void } | null = null;

  const openUpload = (): void => {
    openUploadDialog({
      scope: options.scope,
      ownerId: options.ownerId,
      basePath: options.basePath,
      onDone: () => {
        options.onChanged?.();
        void reload();
      },
    });
  };

  const openLink = (): void => {
    openLinkDialog({
      scope: options.scope,
      ownerId: options.ownerId,
      onDone: () => {
        options.onChanged?.();
        void reload();
      },
    });
  };

  modal = openModal({
    title: options.title,
    wide: true,
    body: [
      h('div', {
        class: 'muted small',
        text:
          options.subtitle ??
          'Slides, PDFs, imagens, áudio, vídeo e playlists. Arquivos ficam em volume dedicado; links do YouTube e Spotify viram players incorporados.',
      }),
      countLabel,
      options.canWrite
        ? h(
            'div',
            { class: 'row' },
            button('Enviar arquivo', { size: 'sm', onClick: openUpload }),
            button('Vincular link (YouTube / Spotify)', {
              size: 'sm',
              variant: 'ghost',
              onClick: openLink,
            }),
          )
        : h('div', {
            class: 'card__hint',
            text: options.readOnlyHint ?? 'Seu perfil tem acesso somente leitura ao repositório.',
          }),
      h('hr', { class: 'separator' }),
      listContainer,
    ],
    footer: [button('Fechar', { variant: 'ghost', onClick: () => modal?.close() })],
  });
}

/* ---------- Envio de arquivo ---------------------------------------------- */

export function openUploadDialog(input0: {
  scope: MediaScope;
  ownerId: string;
  basePath: string;
  onDone: () => void;
}): void {
  const kindSelect = select(
    MEDIA_KINDS.map((kind) => ({ value: kind, label: MEDIA_KIND_LABELS[kind] })),
    { value: 'SLIDES' },
  );
  const titleInput = input({ placeholder: 'Ex.: Slides da ministração (deixe vazio para usar o nome do arquivo)' });
  const fileInput = input({ type: 'file' });
  const errorSlot = h('div', { class: 'field__error' });
  const dropZone = h(
    'div',
    { class: 'dropzone' },
    h('div', { class: 'dropzone__icon', text: '⬆' }),
    h('div', { class: 'dropzone__text', text: 'Arraste o arquivo aqui ou clique para escolher' }),
    h('div', {
      class: 'dropzone__hint',
      text: 'Slides (PPTX/PDF), documentos, imagens, áudio e vídeo — até 500 MB',
    }),
  );

  let selectedFile: File | null = null;

  const setFile = (file: File | null): void => {
    selectedFile = file;
    clear(dropZone);
    if (file) {
      dropZone.append(
        h('div', { class: 'dropzone__icon', text: '✓' }),
        h('div', { class: 'dropzone__text', text: file.name }),
        h('div', { class: 'dropzone__hint', text: formatBytes(file.size) }),
      );
      dropZone.classList.add('dropzone--filled');
      // Sugere o tipo de mídia a partir da extensão do arquivo.
      const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (['ppt', 'pptx', 'key', 'odp'].includes(extension)) kindSelect.value = 'SLIDES';
      else if (extension === 'pdf') kindSelect.value = 'PDF';
      else if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(extension)) kindSelect.value = 'IMAGEM';
      else if (['mp3', 'wav', 'm4a', 'ogg'].includes(extension)) kindSelect.value = 'AUDIO';
      else if (['mp4', 'mov', 'mkv', 'webm'].includes(extension)) kindSelect.value = 'VIDEO';
    } else {
      dropZone.append(
        h('div', { class: 'dropzone__icon', text: '⬆' }),
        h('div', { class: 'dropzone__text', text: 'Arraste o arquivo aqui ou clique para escolher' }),
        h('div', {
          class: 'dropzone__hint',
          text: 'Slides (PPTX/PDF), documentos, imagens, áudio e vídeo — até 500 MB',
        }),
      );
      dropZone.classList.remove('dropzone--filled');
    }
  };

  dropZone.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => setFile(fileInput.files?.[0] ?? null));
  dropZone.addEventListener('dragover', (event) => {
    event.preventDefault();
    dropZone.classList.add('dropzone--active');
  });
  dropZone.addEventListener('dragleave', () => dropZone.classList.remove('dropzone--active'));
  dropZone.addEventListener('drop', (event) => {
    event.preventDefault();
    dropZone.classList.remove('dropzone--active');
    const file = (event as DragEvent).dataTransfer?.files?.[0];
    if (file) setFile(file);
  });

  const submit = async (): Promise<void> => {
    clear(errorSlot);
    const file = selectedFile ?? fileInput.files?.[0] ?? null;

    if (!file) {
      errorSlot.textContent = 'Selecione um arquivo para enviar.';
      return;
    }

    const formData = new FormData();
    formData.append('kind', kindSelect.value);
    formData.append('title', titleInput.value.trim() || file.name);
    formData.append('file', file);

    try {
      await api.upload(input0.basePath, formData);
      modal?.close();
      toast.success('Material anexado', file.name);
      input0.onDone();
    } catch (error) {
      errorSlot.textContent = error instanceof ApiError ? error.message : 'Falha no envio do arquivo.';
    }
  };

  let modal: { close: () => void } | null = null;
  modal = openModal({
    title: 'Enviar material',
    body: [
      h('div', { class: 'form-grid' }, field({ label: 'Tipo de mídia', control: kindSelect, required: true })),
      field({ label: 'Arquivo', control: dropZone, required: true }),
      field({
        label: 'Título',
        control: titleInput,
        hint: 'Como o material aparecerá para a equipe de apoio e no púlpito.',
      }),
      h('div', { class: 'field', style: { display: 'none' } }, fileInput),
      errorSlot,
    ],
    footer: [
      button('Cancelar', { variant: 'ghost', onClick: () => modal?.close() }),
      button('Enviar', { onClick: () => void submit() }),
    ],
  });
}

/* ---------- Vínculo externo ----------------------------------------------- */

export function openLinkDialog(input0: {
  scope: MediaScope;
  ownerId: string;
  onDone: () => void;
  presetProvider?: MediaProvider;
}): void {
  const providerSelect = select(
    [
      { value: 'YOUTUBE', label: 'YouTube (vídeo)' },
      { value: 'SPOTIFY', label: 'Spotify (faixa ou playlist)' },
      { value: 'EXTERNO', label: 'Outro link (Drive, Dropbox, site)' },
    ],
    { value: input0.presetProvider ?? 'YOUTUBE' },
  );
  const urlInput = input({ type: 'url', placeholder: 'https://…' });
  const titleInput = input({ placeholder: 'Ex.: Vídeo de abertura, Playlist de louvor' });
  const kindSelect = select(
    MEDIA_KINDS.map((kind) => ({ value: kind, label: MEDIA_KIND_LABELS[kind] })),
    { value: 'VIDEO' },
  );
  const errorSlot = h('div', { class: 'field__error' });

  // Ajusta o tipo de mídia e valida o domínio conforme o provedor escolhido.
  const syncProvider = (): void => {
    const provider = providerSelect.value as MediaProvider;
    if (provider === 'YOUTUBE') kindSelect.value = 'VIDEO';
    else if (provider === 'SPOTIFY') kindSelect.value = 'PLAYLIST';
    clear(errorSlot);
  };
  providerSelect.addEventListener('change', syncProvider);

  const submit = async (): Promise<void> => {
    clear(errorSlot);
    const url = urlInput.value.trim();
    const provider = providerSelect.value as MediaProvider;

    if (!url) {
      errorSlot.textContent = 'Informe o link.';
      return;
    }
    if (provider === 'YOUTUBE' && !/youtube\.com|youtu\.be/i.test(url)) {
      errorSlot.textContent = 'O link não parece ser do YouTube.';
      return;
    }
    if (provider === 'SPOTIFY' && !/spotify\.com/i.test(url)) {
      errorSlot.textContent = 'O link não parece ser do Spotify.';
      return;
    }

    try {
      await api.post('/api/media/link', {
        scope: input0.scope,
        ownerId: input0.ownerId,
        kind: kindSelect.value,
        title: titleInput.value.trim() || url,
        provider,
        externalUrl: url,
      });
      modal?.close();
      toast.success('Link vinculado', 'O player será incorporado automaticamente.');
      input0.onDone();
    } catch (error) {
      errorSlot.textContent = error instanceof ApiError ? error.message : 'Falha ao vincular o link.';
    }
  };

  let modal: { close: () => void } | null = null;
  modal = openModal({
    title: 'Vincular link de mídia',
    body: [
      h('div', { class: 'form-grid' }, field({ label: 'Origem', control: providerSelect, required: true })),
      field({
        label: 'URL',
        control: urlInput,
        required: true,
        hint: 'YouTube e Spotify são convertidos automaticamente em players incorporados.',
      }),
      h(
        'div',
        { class: 'form-grid' },
        field({ label: 'Tipo de mídia', control: kindSelect }),
        field({ label: 'Título', control: titleInput }),
      ),
      errorSlot,
    ],
    footer: [
      button('Cancelar', { variant: 'ghost', onClick: () => modal?.close() }),
      button('Vincular', { onClick: () => void submit() }),
    ],
  });
}

/* ---------- Utilitários ---------------------------------------------------- */

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}
